import { FormEventHandler } from "react"
import { Link, router, useForm } from "@inertiajs/react"
import { AppLayout } from "../../../components/AppLayout"
import { Badge } from "../../../components/Badge"
import { Field } from "../../../components/Field"
import { SectionCard } from "../../../components/SectionCard"
import { TextInput } from "../../../components/TextInput"

type UserRow = {
  id: number
  name: string
  username: string
  email: string
  phone: string | null
  admin: boolean
  role: "user" | "admin" | "superadmin"
  cards_count: number
}

type Props = {
  users: UserRow[]
  form: {
    name: string
    username: string
    email: string
    phone: string
    password: string
    password_confirmation: string
    admin: boolean
  }
  errors: Record<string, string>
  can_manage_roles?: boolean
  current_user_id: number
}

export default function AdminUsers({ users, form, errors, can_manage_roles: canManageRoles = false, current_user_id: currentUserId }: Props) {
  const { data, setData, post, processing } = useForm({
    name: form.name,
    username: form.username,
    email: form.email,
    phone: form.phone,
    password: form.password,
    password_confirmation: form.password_confirmation,
    admin: form.admin,
    role: canManageRoles && form.admin ? "admin" : "user",
  })

  const submit: FormEventHandler = (event) => {
    event.preventDefault()
    post("/admin/users", {
      preserveScroll: true,
    })
  }

  return (
    <AppLayout title="משתמשים">
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold">משתמשים</h1>
          <p className="mt-1 text-slate-600">יצירה וניהול של משתמשי המערכת</p>
        </div>
        <Link href="/admin" className="btn-secondary">חזרה ללוח הבקרה</Link>
      </div>

      <div className="grid gap-6 lg:grid-cols-[0.95fr_1.3fr]">
        <SectionCard title="יצירת משתמש" description="נשמור את המשתמש ואז ניתן לשייך אליו כרטיס">
          <form onSubmit={submit} className="space-y-4">
            <Field label="שם" error={errors.name}>
              <TextInput value={data.name} onChange={(e) => setData("name", e.target.value)} />
            </Field>
            <Field label="שם משתמש" error={errors.username}>
              <TextInput dir="ltr" value={data.username} onChange={(e) => setData("username", e.target.value)} />
            </Field>
            <Field label="אימייל" error={errors.email}>
              <TextInput type="email" value={data.email} onChange={(e) => setData("email", e.target.value)} />
            </Field>
            <Field label="טלפון" error={errors.phone} hint="לאימות כניסה ב-SMS">
              <TextInput dir="ltr" type="tel" value={data.phone} onChange={(e) => setData("phone", e.target.value)} placeholder="050-0000000" />
            </Field>
            <Field label="סיסמה" error={errors.password}>
              <TextInput type="password" value={data.password} onChange={(e) => setData("password", e.target.value)} />
            </Field>
            <Field label="אימות סיסמה" error={errors.password_confirmation}>
              <TextInput type="password" value={data.password_confirmation} onChange={(e) => setData("password_confirmation", e.target.value)} />
            </Field>
            {canManageRoles ? (
              <label className="block space-y-2">
                <span className="text-sm font-medium text-slate-700">תפקיד</span>
                <select className="input" value={data.role} onChange={(e) => setData("role", e.target.value as typeof data.role)}>
                  <option value="user">משתמש</option>
                  <option value="admin">מנהל</option>
                  <option value="superadmin">מנהל-על</option>
                </select>
              </label>
            ) : null}
            <button disabled={processing} className="btn-primary bg-brand-600">שמירה</button>
          </form>
        </SectionCard>

        <SectionCard title="משתמשים קיימים" description="מי יכול להיכנס למערכת">
          {users.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-slate-500">
              אין עדיין משתמשים
            </div>
          ) : (
            <div className="space-y-3">
              {users.map((user) => (
                <div key={user.id} className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-medium text-slate-900">{user.name}</div>
                      <div className="text-sm text-slate-500">@{user.username}</div>
                      <div className="mt-1 text-sm text-slate-500">{user.email}</div>
                      {user.phone ? <div className="text-sm text-slate-500" dir="ltr">{user.phone}</div> : null}
                    </div>
                    <Badge tone={user.role === "user" ? "slate" : "green"}>
                      {user.role === "superadmin" ? "מנהל-על" : user.role === "admin" ? "מנהל" : "משתמש"}
                    </Badge>
                  </div>
                  <div className="mt-3 text-sm text-slate-500">{user.cards_count} כרטיסים</div>
                  {canManageRoles && user.id !== currentUserId ? (
                    <form
                      onSubmit={(event) => {
                        event.preventDefault()
                        const role = new FormData(event.currentTarget).get("role")
                        router.patch(`/admin/users/${user.id}/role`, { role }, { preserveScroll: true })
                      }}
                      className="mt-3 flex items-center gap-2"
                    >
                      <select name="role" defaultValue={user.role} className="input text-sm">
                        <option value="user">משתמש</option>
                        <option value="admin">מנהל</option>
                        <option value="superadmin">מנהל-על</option>
                      </select>
                      <button className="btn-secondary text-sm">עדכון תפקיד</button>
                    </form>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </div>
    </AppLayout>
  )
}
